import React from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Zap,
  Mic,
  Globe,
  Download,
  Sparkles,
  Music,
  Volume2,
  Star,
  Mail,
  Clock,
} from "lucide-react";

const MODULES = [
  {
    key: "adscript",
    title: "AdScript → Jingle",
    desc: "Paste your ad script, get a production-ready jingle in under 90 seconds.",
    icon: Mic,
  },
  {
    key: "reelsong",
    title: "ReelSong Generator",
    desc: "Optimised for Instagram Reels & YouTube Shorts. Strong 3-second hook, addictive chorus.",
    icon: Zap,
  },
  {
    key: "localads",
    title: "Local Ads Jingle",
    desc: "Hindi-first templates for coaching centres, real estate, retail shops & salons.",
    icon: Volume2,
  },
  {
    key: "brandanthem",
    title: "Brand Anthem",
    desc: "30–60 second emotional anthems with storytelling. Premium tier.",
    icon: Star,
  },
];

const USE_CASES = [
  "Coaching institute", "Real estate", "Retail दुकान", "Salon", "D2C brand", "Quick commerce", "Festival offer", "Reels hook",
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-white text-[#09090B]" data-testid="landing-page">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-black/10">
        <div className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-20 md:pb-28">
          <div className="grid md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-7 fade-up">
              <div className="flex items-center gap-2 mb-6">
                <div className="inline-flex items-center gap-2 px-2.5 py-1 border border-black/15 text-[11px] uppercase tracking-[0.2em] font-bold">
                  <span className="w-1.5 h-1.5 bg-[#FF4500] rounded-full animate-pulse" />
                  Made in India · Supports Multiple Languages
                </div>
              </div>
              <h1 className="font-display text-5xl md:text-7xl font-semibold tracking-tighter leading-[0.95] text-balance">
                Turn your ads into{" "}
                <span className="text-[#FF4500]">catchy songs</span> in seconds.
              </h1>
              <p className="mt-6 text-lg md:text-xl text-zinc-600 max-w-xl leading-relaxed">
                Create high-converting jingles for ads, reels & brand campaigns — no music skills needed. Script → Jingle → Download in under 3 minutes.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Link to="/login" data-testid="hero-cta-link">
                  <Button
                    className="bg-[#FF4500] hover:bg-[#E03C00] text-white rounded-sm h-12 px-6 text-base font-semibold"
                    data-testid="hero-generate-btn"
                  >
                    Generate Your First Free Jingle
                    <ArrowRight size={18} className="ml-2" />
                  </Button>
                </Link>
                <Link
                  to="/pricing"
                  className="text-sm font-semibold underline underline-offset-4 hover:text-[#FF4500]"
                  data-testid="hero-pricing-link"
                >
                  See pricing →
                </Link>
              </div>
              <div className="mt-10 flex items-center gap-6 text-xs uppercase tracking-[0.15em] text-zinc-500 font-bold">
                <span>2 free jingles</span>
                <span className="w-1 h-1 bg-zinc-300 rounded-full" />
                <span>11 Indian languages</span>
                <span className="w-1 h-1 bg-zinc-300 rounded-full" />
                <span>MP3 + WAV download</span>
              </div>
            </div>

            <div className="md:col-span-5 fade-up" style={{ animationDelay: "0.15s" }}>
              <div className="relative aspect-square bg-black border border-black/10 overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_reel-hook/artifacts/fsgsog4e_ChatGPT%20Image%20Apr%2025%2C%202026%2C%2001_12_42%20AM.png"
                  alt="Sound wave"
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-4 left-4 right-4 bg-black/90 backdrop-blur-xl text-white p-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#FF4500] flex items-center justify-center">
                    <Music size={18} />
                  </div>
                  <div className="flex-1 flex items-center gap-[2px] h-8">
                    {Array.from({ length: 32 }).map((_, i) => (
                      <div
                        key={i}
                        className="flex-1 bg-[#FF4500] wave-bar"
                        style={{
                          height: `${30 + ((i * 19) % 65)}%`,
                          animationDelay: `${i * 0.04}s`,
                        }}
                      />
                    ))}
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-widest">
                    Live
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-b border-black/10 bg-[#09090B] text-white py-5 overflow-hidden">
        <div className="flex whitespace-nowrap marquee-track">
          {[...USE_CASES, ...USE_CASES, ...USE_CASES].map((t, i) => (
            <span
              key={i}
              className="font-display text-2xl md:text-3xl font-medium px-8 flex items-center gap-8"
            >
              {t}
              <span className="w-2 h-2 bg-[#FF4500] rounded-full" />
            </span>
          ))}
        </div>
      </section>

      {/* MODULES */}
      <section className="py-20 md:py-28 border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-12 gap-8 mb-14">
            <div className="md:col-span-5">
              <div className="text-sm uppercase tracking-[0.2em] font-bold text-zinc-500 mb-4">
                Four engines · One platform
              </div>
              <h2 className="font-display text-4xl md:text-5xl font-semibold tracking-tighter leading-tight">
                Purpose-built for every kind of ad.
              </h2>
            </div>
            <p className="md:col-span-6 md:col-start-7 text-zinc-600 text-lg leading-relaxed">
              Whether you're a D2C brand, a local shopkeeper, or an agency running performance ads — we've tuned the model for your use case. Pick a module, paste your script, hit generate.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {MODULES.map((m, i) => {
              const Icon = m.icon;
              return (
                <div
                  key={m.key}
                  className="group relative bg-white border border-black/10 p-8 card-hover fade-up"
                  style={{ animationDelay: `${i * 0.08}s` }}
                  data-testid={`module-card-${m.key}`}
                >
                  <div className="flex items-start justify-between mb-6">
                    <div className="w-12 h-12 border border-black/10 flex items-center justify-center group-hover:bg-[#FF4500] group-hover:text-white group-hover:border-[#FF4500] transition-colors">
                      <Icon size={20} />
                    </div>
                    <div className="font-mono text-xs text-zinc-400">
                      0{i + 1}
                    </div>
                  </div>
                  <h3 className="font-display text-2xl font-semibold mb-3">
                    {m.title}
                  </h3>
                  <p className="text-zinc-600 leading-relaxed">{m.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 md:py-28 border-b border-black/10 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-14">
            <div className="text-sm uppercase tracking-[0.2em] font-bold text-zinc-500 mb-4">
              How it works
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-semibold tracking-tighter leading-tight max-w-2xl">
              Idea to jingle in three clean steps.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { n: "01", t: "Write or generate script", d: "Paste your ad copy, or let our AI write a punchy Hinglish script from a one-line product brief." },
              { n: "02", t: "Pick voice, style, duration", d: "Male or female vocalist. Fun, emotional, premium, or urgent. 15 / 20 / 30 seconds." },
              { n: "03", t: "Generate and download", d: "Preview the jingle, read the highlighted hook line, download MP3 or WAV. Ready for reels." },
            ].map((s, i) => (
              <div
                key={i}
                className="bg-white border border-black/10 p-8 card-hover"
                data-testid={`step-${s.n}`}
              >
                <div className="font-mono text-[11px] uppercase tracking-widest text-[#FF4500] font-bold mb-6">
                  Step {s.n}
                </div>
                <h3 className="font-display text-2xl font-semibold mb-3">
                  {s.t}
                </h3>
                <p className="text-zinc-600 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 md:py-28 border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-12 gap-8 mb-14">
            <h2 className="md:col-span-7 font-display text-4xl md:text-5xl font-semibold tracking-tighter leading-tight">
              Loved by marketers & local businesses alike.
            </h2>
            <div className="md:col-span-4 md:col-start-9 flex items-start md:items-end gap-2">
              <div className="flex -space-x-2">
                {[
                  "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?crop=faces&cs=srgb&fm=jpg&w=100&h=100&fit=crop",
                  "https://images.unsplash.com/photo-1544005313-94ddf0286df2?crop=faces&cs=srgb&fm=jpg&w=100&h=100&fit=crop",
                  "https://images.unsplash.com/photo-1695391396401-5fbb4bedafc1?crop=faces&cs=srgb&fm=jpg&w=100&h=100&fit=crop",
                ].map((u, i) => (
                  <img
                    key={i}
                    src={u}
                    alt=""
                    className="w-10 h-10 object-cover border-2 border-white"
                  />
                ))}
              </div>
              <div className="text-sm text-zinc-600">
                <div className="font-semibold text-black">2,400+ jingles</div>
                generated this month
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {[
              {
                q: "Made my coaching institute's reel blow up. 300+ admissions in one month.",
                n: "Rohan Sharma",
                r: "Owner, Bright Futures Coaching",
                i: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?crop=faces&cs=srgb&fm=jpg&w=200&h=200&fit=crop",
              },
              {
                q: "We replaced our ₹50k jingle studio with Jingle Hooks. Same quality, 100x faster.",
                n: "Priya Kapoor",
                r: "Head of Growth, Buzzly D2C",
                i: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?crop=faces&cs=srgb&fm=jpg&w=200&h=200&fit=crop",
              },
              {
                q: "Hinglish jingles my customers actually remember. Sales up 22% in festive season.",
                n: "Amit Verma",
                r: "Owner, Verma Electronics",
                i: "https://images.unsplash.com/photo-1695391396401-5fbb4bedafc1?crop=entropy&cs=srgb&fm=jpg&w=200&h=200&fit=crop",
              },
            ].map((t, i) => (
              <div
                key={i}
                className="bg-white border border-black/10 p-8"
                data-testid={`testimonial-${i}`}
              >
                <div className="flex gap-1 mb-5">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <Star
                      key={j}
                      size={14}
                      fill="#FF4500"
                      strokeWidth={0}
                    />
                  ))}
                </div>
                <p className="font-display text-xl leading-snug mb-8">
                  "{t.q}"
                </p>
                <div className="flex items-center gap-3 pt-5 border-t border-black/10">
                  <img
                    src={t.i}
                    alt={t.n}
                    className="w-10 h-10 object-cover"
                  />
                  <div>
                    <div className="font-semibold text-sm">{t.n}</div>
                    <div className="text-xs text-zinc-500">{t.r}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-[#09090B] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-8">
              <h2 className="font-display text-4xl md:text-6xl font-semibold tracking-tighter leading-[0.95]">
                Your next viral ad jingle is{" "}
                <span className="text-[#FF4500]">90 seconds</span> away.
              </h2>
              <p className="mt-6 text-zinc-400 text-lg max-w-xl">
                Start free. No credit card. Two free jingles on the house.
              </p>
            </div>
            <div className="md:col-span-4 md:text-right">
              <Link to="/login" data-testid="footer-cta-link">
                <Button
                  className="bg-[#FF4500] hover:bg-[#E03C00] text-white rounded-sm h-14 px-8 text-base font-semibold"
                  data-testid="footer-cta-btn"
                >
                  Generate Your First Free Jingle
                  <Sparkles size={18} className="ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#09090B] text-white/80 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-14">
          <div className="grid grid-cols-2 md:grid-cols-12 gap-10">
            {/* Brand */}
            <div className="col-span-2 md:col-span-3">
              <div className="flex items-center gap-0">
                <img
                  src="https://customer-assets.emergentagent.com/job_reel-hook/artifacts/oaoycnla_ChatGPT%20Image%20Apr%2020%2C%202026%2C%2001_20_23%20AM.png"
                  alt="Jingle Hooks"
                  className="h-10 w-10 object-contain shrink-0"
                />
                <span className="font-display text-2xl text-white font-semibold -ml-2">
                  Jingle Hooks
                </span>
              </div>
              <p className="mt-5 text-sm leading-relaxed text-white/60 max-w-xs">
                AI jingle maker & brand song generator. Turn any script into a catchy jingle in seconds — built for Indian brands, reels & local ads.
              </p>
              <div className="mt-5 text-xs uppercase tracking-widest text-white/40 font-mono">
                Made with <span className="text-[#FF4500]">♥</span> in India
              </div>
            </div>

            {/* Product */}
            <div className="md:col-span-2 md:col-start-5">
              <div className="text-xs uppercase tracking-widest text-[#FF4500] font-bold mb-4">Product</div>
              <ul className="space-y-3 text-sm">
                <li><Link to="/" className="hover:text-[#FF4500] transition-colors" data-testid="footer-home-link">Home</Link></li>
                <li><Link to="/about" className="hover:text-[#FF4500] transition-colors" data-testid="footer-about-link">About Us</Link></li>
                <li><Link to="/pricing" className="hover:text-[#FF4500] transition-colors" data-testid="footer-pricing-link">Pricing</Link></li>
                <li><Link to="/generate" className="hover:text-[#FF4500] transition-colors" data-testid="footer-generate-link">Create Jingle</Link></li>
                <li><Link to="/dashboard" className="hover:text-[#FF4500] transition-colors" data-testid="footer-dashboard-link">Dashboard</Link></li>
              </ul>
            </div>

            {/* Languages */}
            <div className="md:col-span-2">
              <div className="text-xs uppercase tracking-widest text-[#FF4500] font-bold mb-4">Languages</div>
              <ul className="space-y-3 text-sm">
                <li><Link to="/jingle/english" className="hover:text-[#FF4500] transition-colors" data-testid="footer-lang-english">English Jingle</Link></li>
                <li><Link to="/jingle/hindi" className="hover:text-[#FF4500] transition-colors" data-testid="footer-lang-hindi">Hindi Jingle</Link></li>
                <li><Link to="/jingle/hinglish" className="hover:text-[#FF4500] transition-colors" data-testid="footer-lang-hinglish">Hinglish Jingle</Link></li>
                <li><Link to="/jingle/tamil" className="hover:text-[#FF4500] transition-colors" data-testid="footer-lang-tamil">Tamil Jingle</Link></li>
                <li><Link to="/jingle/telugu" className="hover:text-[#FF4500] transition-colors" data-testid="footer-lang-telugu">Telugu Jingle</Link></li>
                <li><Link to="/jingle/bengali" className="hover:text-[#FF4500] transition-colors" data-testid="footer-lang-bengali">Bengali Jingle</Link></li>
                <li><Link to="/jingle/marathi" className="hover:text-[#FF4500] transition-colors" data-testid="footer-lang-marathi">Marathi Jingle</Link></li>
                <li><Link to="/jingle/gujarati" className="hover:text-[#FF4500] transition-colors" data-testid="footer-lang-gujarati">Gujarati Jingle</Link></li>
                <li><Link to="/jingle/punjabi" className="hover:text-[#FF4500] transition-colors" data-testid="footer-lang-punjabi">Punjabi Jingle</Link></li>
                <li><Link to="/jingle/kannada" className="hover:text-[#FF4500] transition-colors" data-testid="footer-lang-kannada">Kannada Jingle</Link></li>
                <li><Link to="/jingle/malayalam" className="hover:text-[#FF4500] transition-colors" data-testid="footer-lang-malayalam">Malayalam Jingle</Link></li>
              </ul>
            </div>

            {/* Legal */}
            <div className="md:col-span-2">
              <div className="text-xs uppercase tracking-widest text-[#FF4500] font-bold mb-4">Legal</div>
              <ul className="space-y-3 text-sm">
                <li><Link to="/privacy" className="hover:text-[#FF4500] transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-[#FF4500] transition-colors" data-testid="footer-terms-link">Terms of Service</Link></li>
                <li><Link to="/refund" className="hover:text-[#FF4500] transition-colors" data-testid="footer-refund-link">Refund Policy</Link></li>
                <li><Link to="/cookies" className="hover:text-[#FF4500] transition-colors" data-testid="footer-cookies-link">Cookie Policy</Link></li>
              </ul>
            </div>

            {/* Contact */}
            <div className="md:col-span-3">
              <div className="text-xs uppercase tracking-widest text-[#FF4500] font-bold mb-4">Contact</div>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-2">
                  <Mail size={14} className="text-white/40 shrink-0 mt-0.5" />
                  <a
                    href="mailto:support@jinglehooks.com"
                    className="hover:text-[#FF4500] transition-colors"
                    data-testid="footer-email-link"
                  >
                    support@jinglehooks.com
                  </a>
                </li>
                <li className="flex items-start gap-2">
                  <Clock size={14} className="text-white/40 shrink-0 mt-0.5" />
                  <span>24-hour response time</span>
                </li>
                <li className="flex items-start gap-2">
                  <Globe size={14} className="text-white/40 shrink-0 mt-0.5" />
                  <span>Serving India & global markets</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex flex-col md:flex-row gap-3 items-center justify-between text-xs">
            <div className="font-mono uppercase tracking-widest text-white/40">
              © 2026 Jingle Hooks. All rights reserved.
            </div>
            <div className="font-mono uppercase tracking-widest text-white/40">
              Contact: support@jinglehooks.com
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
